import html2canvas from "html2canvas"
import "jspdf-autotable"
import { PDFHeader } from "../PDFHeader"
import { PDFFooter } from "../PDFFooter"

export const generateInternshipRolesPDF = async ({
  pdf,
  corporateData,
  selectedFilter,
  exportRefs,
  getDynamicLabel
}) => {

  const dynamicLabel = getDynamicLabel("internshipRoles", selectedFilter)

  const filterName =
    selectedFilter === "JOBS"
      ? "Jobs"
      : selectedFilter === "INTERNSHIP"
      ? "Internship"
      : selectedFilter === "INTERNSHIP_PPO"
      ? "Internship + PPO"
      : selectedFilter === "APPRENTICESHIP"
      ? "Apprenticeship"
      : selectedFilter === "PROJECTS"
      ? "Projects"
      : "Roles"

  const allDegreeData = (exportRefs.internshipRolesData?.current || []).map(
    (item) => ({
      degree: item.degree,
      floated: item.floated,
      active: item.active,
      closed: item.closed,
      men: item.degreeGenderAnalysis?.men || 0,
      women: item.degreeGenderAnalysis?.women || 0,
      others: item.degreeGenderAnalysis?.others || 0,
    })
  )

  if (allDegreeData.length === 0) return

  const ITEMS_PER_PAGE = 10
  const totalPages = Math.ceil(allDegreeData.length / ITEMS_PER_PAGE)

  for (let page = 0; page < totalPages; page++) {

    if (page > 0) {
      pdf.addPage("a4", "landscape")
    }

    const pageData = allDegreeData.slice(
      page * ITEMS_PER_PAGE,
      (page + 1) * ITEMS_PER_PAGE
    )

    const startY = await PDFHeader({ pdf, corporateData })

    pdf.setFontSize(14)
    pdf.setFont(undefined, "bold")
    pdf.text(
      totalPages > 1
        ? `${dynamicLabel} (${page + 1}/${totalPages})`
        : dynamicLabel,
      15,
      startY
    )

    pdf.setFontSize(10)
    pdf.setFont(undefined, "normal")
    pdf.text(
      `Data below shows the indication of ${filterName.toLowerCase()} roles distribution by degree`,
      15,
      startY + 6
    )

    let currentY = startY + 15

    const setChartRange = exportRefs.setChartRange?.current

    if (setChartRange) {
      setChartRange({
        start: page * ITEMS_PER_PAGE,
        count: pageData.length,
      })

      await new Promise((resolve) => setTimeout(resolve, 800))
    }

    const element = exportRefs.internshipRoles?.current

    if (element) {

      const canvas = await html2canvas(element, { scale: 2 })

      const imgData = canvas.toDataURL("image/png")

      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = pageWidth - 30
      let imgHeight = (canvas.height * imgWidth) / canvas.width
      const maxImgHeight = pageHeight - currentY - 75

      if (imgHeight > maxImgHeight) {
        imgHeight = maxImgHeight
      }

      pdf.addImage(
        imgData,
        "PNG",
        15,
        currentY - 5,
        imgWidth,
        imgHeight
      )

      currentY += imgHeight + 2
    }

    addInternshipRolesTablesForPage(pdf, currentY, pageData)

    await PDFFooter({ pdf })
  }

  const setChartRangeReset = exportRefs.setChartRange?.current
  if (setChartRangeReset) {
    setChartRangeReset(null)
  }
}

const addInternshipRolesTablesForPage = (pdf, startY, degreeData) => {

  const pageWidth = pdf.internal.pageSize.getWidth()
  const margin = 12
  const availableWidth = pageWidth - margin * 2

  const statusColWidth = 22
  const dataColWidth = Math.floor(
    (availableWidth - statusColWidth) / degreeData.length
  )

  const roleHeaders = [
    { header: "Status", dataKey: "status" },
    ...degreeData.map((d, i) => ({
      header: d.degree,
      dataKey: `c${i}`,
    })),
  ]

  const roleDataRows = [
    {
      status: "Floated",
      ...degreeData.reduce(
        (a, d, i) => ({ ...a, [`c${i}`]: d.floated }),
        {}
      ),
    },
    {
      status: "Active",
      ...degreeData.reduce(
        (a, d, i) => ({ ...a, [`c${i}`]: d.active }),
        {}
      ),
    },
    {
      status: "Closed",
      ...degreeData.reduce(
        (a, d, i) => ({ ...a, [`c${i}`]: d.closed }),
        {}
      ),
    },
  ]

  const roleColumnStyles = {
    status: { cellWidth: statusColWidth, halign: "left" },
  }

  degreeData.forEach((_, i) => {
    roleColumnStyles[`c${i}`] = {
      cellWidth: dataColWidth,
      halign: "center",
    }
  })

  pdf.autoTable({
    startY: startY,
    columns: roleHeaders,
    body: roleDataRows,
    theme: "grid",
    styles: { fontSize: 8, textColor: [40, 40, 40], cellPadding: 2 },
    headStyles: {
      fillColor: [238, 238, 238],
      textColor: 0,
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles: roleColumnStyles,
    tableWidth: "wrap",
    margin: { left: margin, right: margin },

    didParseCell: function (data) {
      if (data.column.dataKey === "status") {
        data.cell.styles.fillColor = [238, 238, 238]
        data.cell.styles.fontStyle = "bold"
        data.cell.styles.cellPadding = { left: 8 }
        data.cell.styles.valign = "middle"
      }
    },

    didDrawCell: function (data) {

      if (data.column.dataKey === "status" && data.cell.section === "body") {

        const { x, y, height } = data.cell
        const status = data.cell.raw

        let color = [0, 0, 0]

        if (status === "Floated") color = [0, 31, 84]
        if (status === "Active") color = [173, 232, 244]
        if (status === "Closed") color = [180, 180, 180]

        pdf.setFillColor(...color)

        pdf.circle(x + 3, y + height / 2, 1.2, "F")
      }
    },
  })

  let currentY = pdf.autoTable.previous.finalY + 3

  const genderColumnStyles = {
    0: { cellWidth: statusColWidth, halign: "left", fontStyle: "bold" },
  }

  degreeData.forEach((_, i) => {
    genderColumnStyles[i + 1] = {
      cellWidth: dataColWidth,
      halign: "center",
    }
  })

  pdf.autoTable({
    startY: currentY,
    body: [
      ["Men", ...degreeData.map((d) => d.men)],
      ["Women", ...degreeData.map((d) => d.women)],
      ["Others", ...degreeData.map((d) => d.others)],
    ],
    showHead: false,
    theme: "grid",
    styles: { fontSize: 8, textColor: [40, 40, 40], cellPadding: 2 },
    columnStyles: genderColumnStyles,
    tableWidth: "wrap",
    margin: { left: margin, right: margin },

    didParseCell: function (data) {
      if (data.column.index === 0) {
        data.cell.styles.cellPadding = { left: 7, top: 2, bottom: 2 }
        data.cell.styles.valign = "middle"
      }
    },

    didDrawCell: function (data) {

      if (data.column.index === 0 && data.cell.section === "body") {

        const { x, y, height } = data.cell
        const value = data.cell.raw

        const radius = 1
        const circleX = x + 3
        const circleY = y + height / 2

        if (value === "Men") {
          pdf.setFillColor(13, 58, 133)
          pdf.circle(circleX, circleY, radius, "F")
        }

        if (value === "Women") {
          pdf.setFillColor(1, 138, 190)
          pdf.circle(circleX, circleY, radius, "F")
        }
        if (value === "Others") {
          pdf.setFillColor(145, 225, 255)
          pdf.circle(circleX, circleY, radius, "F")
        }
      }
    },
  })
}